import { RouteObject } from "react-router-dom";
import { FinancesPage } from "src/routes/billing/FinancesPage";
import { InvoiceTermsForm } from "src/routes/billing/invoice-terms/components/InvoiceTermsForm";
import { BillEditor } from "src/routes/bills/BillEditor";
import { NotFound } from "src/routes/NotFound";
import { AppShell } from "src/routing/components/AppShell";
import { GlobalNavLayout } from "src/routing/components/layouts/GlobalNavLayout";
import { ScrollableParentLayout } from "src/routing/components/layouts/ScrollableParentLayout";
import {
  billsRoute,
  createBillRoute,
  invoiceTermRoute,
  invoiceTermsRoute,
  invoicesRoute,
  lienWaiversRoute,
  purchaseOrdersRoute,
} from "src/routing/domains/financesPaths";

/**
 * Top-level Finances route trees (`/bills`, `/invoices`, `/purchase-orders`, `/invoice-terms`, `/lien-waivers`).
 *
 * Role: data router domain route.
 *
 * Legacy stack (pages still use `PageHeader`, not Beam page layouts):
 * `AppShell` → `GlobalNavLayout` → `ScrollableParentLayout` → page.
 * When pages move to `PageHeaderLayout` / `GridTableLayout`, switch to
 * `AppShell` → `BeamNavbarLayout` → page (see `domains/projects.ts`).
 *
 * Path constants live in `financesPaths.ts` (imported by `RouteUrls.ts` / pages) to avoid a cycle
 * through this file's page `Component` imports.
 * Legacy `Routes.tsx` must not register these paths.
 */
const financesTabLayout: RouteObject = {
  Component: GlobalNavLayout,
  children: [{ Component: ScrollableParentLayout, children: [{ index: true, Component: FinancesPage }] }],
};

const billsDomainRoute: RouteObject = {
  path: billsRoute.path,
  Component: AppShell,
  children: [
    {
      Component: GlobalNavLayout,
      children: [
        {
          Component: ScrollableParentLayout,
          children: [
            { index: true, Component: FinancesPage },
            // BillEditor also renders BillPage for non-editable bills.
            { path: createBillRoute.path, Component: BillEditor },
          ],
        },
      ],
    },
    { path: "*", Component: NotFound },
  ],
};

const invoicesDomainRoute: RouteObject = {
  path: invoicesRoute.path,
  Component: AppShell,
  children: [financesTabLayout, { path: "*", Component: NotFound }],
};

const purchaseOrdersDomainRoute: RouteObject = {
  path: purchaseOrdersRoute.path,
  Component: AppShell,
  children: [financesTabLayout, { path: "*", Component: NotFound }],
};

const invoiceTermsDomainRoute: RouteObject = {
  path: invoiceTermsRoute.path,
  Component: AppShell,
  children: [
    {
      Component: GlobalNavLayout,
      children: [
        {
          Component: ScrollableParentLayout,
          children: [
            { index: true, Component: FinancesPage },
            { path: `${invoiceTermRoute.path}/*`, Component: InvoiceTermsForm },
          ],
        },
      ],
    },
    { path: "*", Component: NotFound },
  ],
};

const lienWaiversDomainRoute: RouteObject = {
  path: lienWaiversRoute.path,
  Component: AppShell,
  children: [financesTabLayout, { path: "*", Component: NotFound }],
};

/**
 * Migrated Finances route trees registered in `Router.tsx`.
 */
export const financesRoutes: RouteObject[] = [
  billsDomainRoute,
  invoicesDomainRoute,
  purchaseOrdersDomainRoute,
  invoiceTermsDomainRoute,
  lienWaiversDomainRoute,
];
